// @flow
import React from 'react'
import I18n from 'i18n-react'
import Select from 'react-select/lib/Select'
import classNames from 'classnames'
import VirtualizedSelect from 'react-virtualized-select'
import String from './String'

import AbstractFormField from './AbstractFormField'
import { capitalizeFirstLetter } from '../../InputHelpFunctions'
import { getValueForPath } from '../../FilterFunctions'
import { sortObjectArrayByProp } from '../../SortFunctions'

// types
import type { ResourcesType } from '../../Types/Resources'
import type { DefaultFormFieldState, DefaultFormFieldType } from './AbstractFormField'

type StringEnumRefProps = DefaultFormFieldType & {
  domain: {
    _type: string,
    values: Array<string>,
    resourceName: string,
  },
  options: any,
  resources: ResourcesType,
  translated: boolean,
  value: {
    [key: string]: string,
  },
  sortByLabels?: boolean,
  sortValues?: boolean,
  virtualized?: boolean,
  matchPos?: string,
  clearAllText?: string,
  clearValueText?: string,
  noResultsText?: string,
  placeholder?: string,
  searchPromptText?: string,
}

type SelectedValue = {
  [key: string]: string,
}

type StringEnumRefState = DefaultFormFieldState & {
  options?: Array<SelectedValue>,
  prevPropsValue: string,
}

export default class StringEnumRef extends AbstractFormField<StringEnumRefProps, StringEnumRefState> {
  static defaultProps = {
    clearAllText: 'stringEnum.clearAllText',
    clearValueText: 'stringEnum.clearValueText',
    matchPos: 'start',
    noResultsText: 'stringEnum.noResultsText',
    placeholder: 'stringEnum.placeholder',
    searchPromptText: 'stringEnum.searchPromptText',
  }

  static getDerivedStateFromProps(nextProps: StringEnumRefProps, prevState: StringEnumRefState) {
    let changed = false
    const newState = {}
    let resourcesValue = []
    if (nextProps.values) {
      resourcesValue = nextProps.values
    } else if (nextProps.resources) {
      resourcesValue = getValueForPath(nextProps.domain.resourceName, 0, nextProps.resources)
      if (!resourcesValue) {
        resourcesValue = []
      }
    }
    if (resourcesValue.asMutable) {
      resourcesValue = resourcesValue.asMutable()
    }
    if (Array.isArray(resourcesValue) && resourcesValue.length) {
      let labels = {}
      let options = []

      if (nextProps.sortValues) {
        resourcesValue.sort((a, b) => a - b)
      }

      if (nextProps.valueNames && !nextProps.translated) {
        labels = resourcesValue.reduce((acc, value) => {
          let label = capitalizeFirstLetter(nextProps.valueNames[value] ? nextProps.valueNames[value] : value)
          acc[value] = label
          options.push({ value, label })
          return acc
        }, {})
      } else {
        labels = resourcesValue.reduce((acc, value) => {
          acc[value] = !nextProps.translated
            ? capitalizeFirstLetter(value)
            : I18n.translate(`valueTranslations.${nextProps.name}.${value}`)
          options.push({ value, label: acc[value] })
          return acc
        }, {})
      }

      if (nextProps.sortByLabels) {
        options = sortObjectArrayByProp(options, 'label')
      }

      changed = true
      newState.labels = labels
      newState.value = null
      newState.options = options
      if (nextProps.value) {
        newState.value = { value: nextProps.value, label: labels[nextProps.value] }
      }
    }

    if (nextProps.value !== prevState.prevPropsValue && !!nextProps.value && !!prevState.prevPropsValue) {
      changed = true
      newState.prevPropsValue = nextProps.value
    }

    if (
      !nextProps.isPristine !== !prevState.pristine &&
      nextProps.isPristine !== prevState.prevIsPristine &&
      !nextProps.isPristine
    ) {
      changed = true
      newState.pristine = false
      newState.prevIsPristine = nextProps.isPristine
    }

    if (I18n.texts && I18n.texts.translationLoaded !== prevState.translationsLoaded) {
      changed = true
      newState.translationsLoaded = I18n.texts.translationLoaded
    }

    return changed ? newState : null
  }

  constructor(props: StringEnumRefProps) {
    super(props)
    this.state = {
      filterOptions: [],
      options: [],
      prevPropsValue: '',
      pristine: true,
      value: null,
      translationsLoaded: false,
    }
    this.selectRef = React.createRef()
  }

  handleOnChange = (value: SelectedValue) => {
    if (value) {
      this.setState({ value })
      this.props.onChange(value.value)
    } else {
      this.setState({ value: {} })
      this.props.onChange('')
    }
  }

  handleOnChangeInput = (value) => {
    if (value) {
      this.props.onChange(value)
    } else {
      this.props.onChange('')
    }
  }

  handleOnBlur = (event: SyntheticInputEvent<HTMLInputElement>) => {
    this.onBlur(event)
    if (event.target.value) {
      this.selectRef.current.selectFocusedOption()
    }
  }

  handleOnBlurInput = (name, value) => {
    this.onBlur({ target: { value } })
  }

  render() {
    if (this.props.textMode) {
      let label = this.props.value
      if (this.state.labels && this.state.labels[this.props.value]) {
        label = this.state.labels[this.props.value]
      }
      return <span>{label}</span>
    }

    const classes = [this.props.virtualized ? 'react-select react-virtualized-select' : 'react-select']
    if (this.hasErrors() && !this.isPristine()) {
      classes.push('is-invalid')
    }

    const globalAttributes = {
      disabled: !this.props.enabled,
      name: this.props.name,
      placeholder: I18n.translate(this.props.placeholder),
      onBlur: this.handleOnBlur,
      onChange: this.handleOnChange,
      value: this.state.value,
      ref: this.selectRef,
      clearable: false,
    }

    const virtualizedAttributes = {
      autosize: false,
      clearAllText: I18n.translate(this.props.clearAllText),
      clearValueText: I18n.translate(this.props.clearValueText),
      noResultsText: I18n.translate(this.props.noResultsText),
      searchPromptText: I18n.translate(this.props.searchPromptText),
      options: this.state.options,
      matchPos: this.props.matchPos,
    }

    if (this.props.renderInput) {
      return (
        <String
          {...globalAttributes}
          className="form-control"
          enabled={!this.props.readOnly}
          id={this.props.id}
          inputClassName={this.props.inputClassName}
          inputType={this.props.inputType}
          isPristine={this.props.isPristine}
          onBlur={this.handleOnBlurInput}
          onChange={this.handleOnChangeInput}
          prefix={this.props.prefix}
          textMode={this.props.textMode}
          validationErrors={this.props.validationErrors}
          value={this.props.value}
        />
      )
    }

    if (this.props.virtualized) {
      return (
        <div className={classNames(classes)} id={this.props.id}>
          <VirtualizedSelect {...globalAttributes} {...virtualizedAttributes} />
          {this.renderError()}
        </div>
      )
    }

    return (
      <div className={classNames(classes)} id={this.props.id}>
        <Select {...globalAttributes} {...virtualizedAttributes} />
        {this.renderError()}
      </div>
    )
  }
}
